/**
 * downloadFile — ONE way to hand the user a server-generated file
 * (CSV/PDF export). On the WEB, navigating works: cookies ride along
 * and the browser shows a download. In the NATIVE apps every export
 * used to do ``window.location.href = /api/...`` — the WebView
 * navigated away from the SPA with no cookie auth and rendered a
 * blank page (annotator-reported). Here we fetch through the wrapped
 * fetch (JWT attached), then hand the bytes over:
 *   1. Web Share API with a File (Android WebView + iOS 15+) — opens
 *      the share sheet: save to Files/Drive, WhatsApp, email…
 *   2. Fallback: object-URL anchor click (desktop shell, older webviews).
 */
import { isCapacitor } from "./host";

const guessName = (url: string, res: Response): string => {
  const cd = res.headers.get("content-disposition") || "";
  const m = cd.match(/filename\*?=(?:UTF-8''|")?([^";]+)/i);
  if (m) return decodeURIComponent(m[1].replace(/"/g, ""));
  const u = new URL(url, window.location.origin);
  const base = u.pathname.split("/").filter(Boolean).pop() || "export";
  const fmt = u.searchParams.get("export") || "";
  return /\.\w+$/.test(base) ? base : `${base}${fmt ? `.${fmt}` : ".csv"}`;
};

export const downloadFile = async (url: string, filename?: string): Promise<void> => {
  if (!isCapacitor()) {
    // Browser + desktop shell: same-origin navigation with cookies.
    window.location.href = url;
    return;
  }
  const res = await fetch(url, { headers: { Accept: "*/*" }, cache: "no-store" });
  if (!res.ok) throw new Error(`Export failed (${res.status})`);
  const blob = await res.blob();
  const name = filename || guessName(url, res);
  const file = new File([blob], name, { type: blob.type || "application/octet-stream" });
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const nav = navigator as any;
  if (nav.canShare?.({ files: [file] }) && nav.share) {
    try {
      await nav.share({ files: [file], title: name });
      return;
    } catch (e) {
      // AbortError = user closed the sheet — done, not an error.
      if ((e as Error)?.name === "AbortError") return;
    }
  }
  const obj = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = obj;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(obj), 30_000);
};
